import React from 'react';
import { Card, CardBody } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { formatDuration } from './formatters';

/**
 * Reglas de salud evaluadas en cliente sobre la respuesta de /v1/metrics.
 * Devuelve un array de alertas con { level: 'danger'|'warning'|'info', icon, title, detail }.
 *
 * Pensadas para que el operador detecte de un vistazo:
 *   - desfases de cola (BD <-> Redis)
 *   - workers caídos / zombie
 *   - backlog acumulándose (queue_wait p95 alto)
 *   - tasa de fallos elevada
 *   - fallos tomando mucho más que éxitos (timeouts/zombies)
 */
function evaluateAlerts(data, expectedWorkers, t) {
  if (!data) return [];
  const alerts = [];
  const queue = data.queue_now || {};
  const tp = data.throughput || {};
  const proc = data.processing_time_seconds || {};
  const wait = data.queue_wait_seconds || {};

  // 1) Desfase BD <-> Redis: jobs marcados queued pero no encolados (perdidos).
  const dbQ = Number(queue.db_queued || 0);
  const redisLen = Number(queue.redis_len || 0);
  const drift = Math.abs(dbQ - redisLen);
  if (drift >= 5) {
    alerts.push({
      level: dbQ > redisLen ? 'danger' : 'warning',
      icon: 'fas fa-triangle-exclamation',
      title:
        dbQ > redisLen
          ? t('Inconsistencia BD↔Redis: {{n}} jobs perdidos', { n: drift })
          : t('Desfase BD↔Redis ({{n}} jobs en Redis sin BD)', { n: drift }),
      detail: `db_queued=${dbQ} vs redis_len=${redisLen}. ${
        dbQ > redisLen
          ? t('Jobs marcados queued en Postgres pero no presentes en Redis: requieren reencolar.')
          : t('Hay más jobs en Redis que en BD. Probable carrera de inserción.')
      }`,
    });
  }

  // 2) Workers vivos vs esperado.
  const alive = Number(queue.workers_alive || 0);
  if (alive < expectedWorkers) {
    alerts.push({
      level: 'danger',
      icon: 'fas fa-server',
      title: t('Workers caídos: {{alive}} / {{expected}} esperados', { alive, expected: expectedWorkers }),
      detail: t('Uno o más workers RQ no están registrados como vivos en Redis. Revisar containers y healthchecks.'),
    });
  } else if (alive > expectedWorkers) {
    alerts.push({
      level: 'warning',
      icon: 'fas fa-ghost',
      title: t('Workers zombie detectados ({{alive}} vs {{expected}} esperados)', { alive, expected: expectedWorkers }),
      detail: t('Hay workers registrados de más en Redis (probables zombies de despliegues anteriores). Considerar Worker.cleanup() o `rq worker --burst`.'),
    });
  }

  // 3) Backlog: queue_wait.total.p95 alto.
  const waitP95 = Number(wait.total?.p95 || 0);
  if (waitP95 > 300) {
    alerts.push({
      level: 'danger',
      icon: 'fas fa-hourglass-half',
      title: t('Backlog severo: espera p95 = {{dur}}', { dur: formatDuration(waitP95) }),
      detail: t('El p95 de espera en cola supera los 5 minutos. Hay acumulación. Revisa workers_alive, throughput y si la tasa de ingreso supera la de procesamiento.'),
    });
  } else if (waitP95 > 60) {
    alerts.push({
      level: 'warning',
      icon: 'fas fa-hourglass-half',
      title: t('Espera elevada: p95 = {{dur}}', { dur: formatDuration(waitP95) }),
      detail: t('El p95 de espera en cola supera 1 minuto. Si sostenido, puede indicar saturación de workers.'),
    });
  }

  // 4) Tasa de fallos elevada.
  const total = Number(tp.total || 0);
  const failed = Number(tp.failed || 0);
  if (total > 0) {
    const failRate = failed / total;
    if (failRate > 0.25) {
      alerts.push({
        level: 'danger',
        icon: 'fas fa-circle-xmark',
        title: t('Tasa de fallos crítica: {{pct}}%', { pct: (failRate * 100).toFixed(1) }),
        detail: t('{{failed}} de {{total}} jobs fallaron en la ventana. Revisar logs de workers y categorías en "Resultado final".', { failed, total }),
      });
    } else if (failRate > 0.15) {
      alerts.push({
        level: 'warning',
        icon: 'fas fa-circle-exclamation',
        title: t('Tasa de fallos elevada: {{pct}}%', { pct: (failRate * 100).toFixed(1) }),
        detail: t('{{failed}} de {{total}} jobs fallaron en la ventana.', { failed, total }),
      });
    }
  }

  // 5) Fallos tardando mucho mas que exitos.
  const doneP95 = Number(proc.done?.p95 || 0);
  const failedP95 = Number(proc.failed?.p95 || 0);
  if (doneP95 > 0 && failedP95 > doneP95 * 1.5 && failedP95 > 20) {
    alerts.push({
      level: 'info',
      icon: 'fas fa-stopwatch',
      title: t('Fallos tomando {{mult}}× más que éxitos', { mult: (failedP95 / doneP95).toFixed(1) }),
      detail: t('processing_time p95 — done: {{done}} vs failed: {{failed}}. Posible patrón de timeouts o zombies.', { done: formatDuration(doneP95), failed: formatDuration(failedP95) }),
    });
  }

  return alerts;
}

const LEVEL_STYLE = {
  danger: { bg: 'rgba(220, 38, 38, 0.12)', border: '#DC2626', text: '#991B1B', dot: '#DC2626' },
  warning: { bg: 'rgba(245, 158, 11, 0.12)', border: '#F59E0B', text: '#92400E', dot: '#F59E0B' },
  info: { bg: 'rgba(59, 130, 246, 0.12)', border: '#3B82F6', text: '#1E3A8A', dot: '#3B82F6' },
  ok: { bg: 'rgba(16, 185, 129, 0.10)', border: '#10B981', text: '#065F46', dot: '#10B981' },
};

const HealthAlerts = ({ data, expectedWorkers = 4 }) => {
  const { t } = useTranslation();
  const alerts = evaluateAlerts(data, expectedWorkers, t);

  if (!alerts.length) {
    const s = LEVEL_STYLE.ok;
    return (
      <Card className="mb-3 ai-perf-alerts ai-perf-alerts--ok">
        <CardBody className="d-flex align-items-center gap-2 py-2">
          <span
            className="ai-perf-dot ai-perf-dot--live"
            style={{ background: s.dot, boxShadow: `0 0 0 4px ${s.bg}` }}
          />
          <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
            {t('Sistema OK')}
          </span>
          <small style={{ color: 'var(--text-secondary)' }}>
            {t('Sin alertas en este snapshot.')}
          </small>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="mb-3 ai-perf-alerts">
      <CardBody className="py-2 d-flex flex-column gap-2">
        {alerts.map((a, i) => {
          const s = LEVEL_STYLE[a.level] || LEVEL_STYLE.info;
          return (
            <div
              key={i}
              className="ai-perf-alert d-flex align-items-start gap-2"
              style={{
                background: s.bg,
                border: `1px solid ${s.border}`,
                padding: '8px 12px',
                borderRadius: 6,
              }}
            >
              <i className={a.icon} style={{ color: s.border, marginTop: 3 }} />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 13 }}>
                  {a.title}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: 'var(--text-secondary)',
                    marginTop: 2,
                  }}
                >
                  {a.detail}
                </div>
              </div>
            </div>
          );
        })}
      </CardBody>
    </Card>
  );
};

export default HealthAlerts;
